import { useEffect, useState } from "react";
import {
  fetchReport,
  fetchRecentReports,
  generateReport,
  type DailyReport,
  type DailyReportSummary
} from "../lib/api";
import { todayLocal, formatDateVi } from "../lib/format";
import { StatusOrb, statusLabel } from "../components/StatusOrb";
import { Markdown } from "../components/Markdown";

export function Home() {
  const today = todayLocal();
  const [report, setReport] = useState<DailyReport | null>(null);
  const [recent, setRecent] = useState<DailyReportSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeDate, setActiveDate] = useState(today);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([fetchReport(activeDate), fetchRecentReports(14)])
      .then(([r, rec]) => {
        if (cancelled) return;
        setReport(r);
        setRecent(rec);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeDate]);

  async function onGenerate() {
    setGenerating(true);
    setError(null);
    try {
      const r = await generateReport(activeDate, true);
      setReport(r);
      const rec = await fetchRecentReports(14);
      setRecent(rec);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div>
      {/* Hero tile — light canvas, Apple `product-tile-light` */}
      <section className="bg-canvas px-6 py-section md:px-12">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-captionStrong uppercase tracking-wide text-ink-muted48">
            {formatDateVi(activeDate)}
          </p>
          {loading ? (
            <div className="mt-6 h-12 animate-pulse rounded-md bg-canvas-parchment" />
          ) : report ? (
            <>
              <div className="mt-6 flex items-center justify-center gap-4">
                <StatusOrb status={report.status} size={20} />
                <h2 className="text-hero">{statusLabel(report.status)}</h2>
              </div>
              <p className="mt-6 text-lead text-ink">{report.headline}</p>
            </>
          ) : (
            <>
              <h2 className="mt-6 text-hero">Chưa có báo cáo</h2>
              <p className="mt-6 text-lead text-ink-muted80">
                Hôm nay chưa có dữ liệu, hoặc báo cáo chưa được tạo.
              </p>
              <div className="mt-10">
                <button
                  onClick={onGenerate}
                  disabled={generating}
                  className="apple-press rounded-pill bg-action px-[22px] py-[11px] text-bodyApple text-white disabled:opacity-50"
                >
                  {generating ? "Đang tạo..." : "Tạo báo cáo bây giờ"}
                </button>
              </div>
            </>
          )}
          {error && (
            <p className="mt-6 text-captionApple text-[#a84a3a]">{error}</p>
          )}
        </div>
      </section>

      {/* Body tile — parchment, holds the markdown report */}
      {report && (
        <section className="bg-canvas-parchment px-6 py-section md:px-12">
          <div className="mx-auto max-w-3xl">
            <Markdown md={report.body_md} />
            <div className="mt-12 flex items-center gap-3 text-captionApple text-ink-muted48">
              <span>Tạo bởi {report.model}</span>
              <span>·</span>
              <span>
                {new Date(report.generated_at).toLocaleString("vi-VN", {
                  timeZone: "Asia/Tokyo"
                })}
              </span>
              <span>·</span>
              <button
                onClick={onGenerate}
                disabled={generating}
                className="apple-press text-action hover:underline"
              >
                {generating ? "Đang tạo lại..." : "Tạo lại"}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Recent reports tile — dark, Apple `product-tile-dark` */}
      {recent.length > 0 && (
        <section className="bg-tile-1 px-6 py-section text-white md:px-12">
          <div className="mx-auto max-w-3xl">
            <h3 className="text-displayMd">14 ngày gần đây</h3>
            <ul className="mt-10 divide-y divide-white/10">
              {recent.map((r) => (
                <li key={r.date_local}>
                  <button
                    onClick={() => setActiveDate(r.date_local)}
                    className="apple-press flex w-full items-center justify-between py-4 text-left"
                  >
                    <span className="flex items-center gap-4">
                      <StatusOrb status={r.status} />
                      <span className="text-bodyApple">{formatDateVi(r.date_local)}</span>
                    </span>
                    <span className="text-captionApple text-bodyMuted line-clamp-1 max-w-[60%]">
                      {r.headline}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}
    </div>
  );
}
